import mathjs = require("mathjs");
type Filler<T> = (i: number, j: number) => T;

export class Matrix<T> {
    private _rows: number;
    private _cols: number;
    get rows() {
        return this._rows;
    }
    get cols() {
        return this._cols;
    }

    get raw() {
        return this._entries;
    }

    multiply(other: Matrix<T>): Matrix<T> {
        let rows = this.rows;
        let cols = other.cols;
        let m = Matrix.make<T>(rows, cols, this.fill, this.formatter);
        let entries;
        try {
            entries = mathjs.multiply(this._entries, other._entries);
        } 
        catch (err) {
            entries = [[]];
        }

        m._entries = entries as any;
        return m;
    }

    public formatter: (entry: T) => string;
    private fill: Filler<T>;
    constructor() {}

    static deserialize<T>(json: string, fill?: Filler<T>, formatter?: (e: T) => string): Matrix<T> {
        let entries = JSON.parse(json);
        let rows = entries.length;
        let cols = 0;
        if (rows > 0) {
            cols = rows[0].length;
        }
        let m = Matrix.make<T>(rows, cols);
        m._entries = entries;
        return m;
    }
    static make<T>(rows: number = 0, cols: number = 0, fill?: Filler<T>, formatter?: (entry: T) => string): Matrix<T> {
        let m = new Matrix<T>();
        m._rows = 0;
        m._cols = 0;
        m.fill = fill ? fill : () => undefined;
        m.formatter = formatter ? formatter : v => JSON.stringify(v);
        m.grow(rows, cols, fill);
        return m;
    }

    private _entries: T[][] = [];
    private checkBounds(i: number, j: number): string {
        let errs = [];
        if (i > this.rows - 1) errs.push(`${i} > ${this.rows - 1}`);
        if (j > this.cols - 1) errs.push(`${j} > ${this.cols - 1}`);

        return errs.join(",");
    }
    setEntry(i: number, j: number, val: T): this {
        let err = this.checkBounds(i, j);
        if (err) throw new RangeError(err);
        
        this._entries[i][j] = val;
        return this;
    }

    getEntry(i: number, j: number): T | undefined {
        if (this.checkBounds(i, j)) {
            return undefined;
        }
        else {
            return this._entries[i][j];
        }
    }
    forEachEntry(fn: (val: T, i: number, j: number) => void) {
        for (let i = 0; i < this.rows; i++) {
            for (let j = 0; j < this.cols; j++) {
                fn(this.getEntry(i, j), i, j);
            }
        }
    }

    serialize(): string {
        return JSON.stringify(this.raw);
    }

 
    map<Q>(fn: (val: T, i: number, j: number) => Q, fill?: Filler<Q>): Matrix<Q> {
        let m = new Matrix<Q>();
        m._rows = this.rows;
        m._cols = this.cols;
        m.fill = fill;
        m._entries = this._entries.map((row, i) => row.map((entry, j) => fn(entry, i, j)));
        return m;
    }

    flatten(): T[] {
        return this._entries.reduce((p, n) => p.concat(n), []);
    }
    clone(): Matrix<T> {
        let m = new Matrix<T>();
        m._rows = this.rows;
        m._cols = this.cols;
        m.fill = this.fill ? this.fill : () => undefined;
        m._entries = this._entries.map(row => row.slice());

        return m;
    }

    grow(rows: number = 0, cols: number = 0, fill?: T | Filler<T>): this {
        let or = this._entries.length;
        let oc = or > 0 ? this._entries[0].length : 0;
        this._rows = this._rows + rows;
        this._cols = this._cols + cols;
        let filler: Filler<T>;
        if (typeof fill === "function") {
            filler = fill;
        }
        else if (fill != null) {
            filler = () => fill;
        }
        else {
            filler = this.fill;
        }
        // Fill the old and new columns of every new row
        for (let i = or; i < this.rows; i++) {
            this._entries[i] = new Array(this._cols);
            for (let j = 0; j < this.cols; j++) {
                this._entries[i][j] = filler(i, j);
            }
        }

        // fill the new columns of every old row
        for (let p = 0; p < or; p++) {
            let concat = [];
            for (let q = oc; q < this._cols; q++) {
                concat.push(filler(p, q));
            }
            this._entries[p] = this._entries[p].concat(concat);
        }
        return this;
    }

    removeRows(startIndex: number, howMany = 1) {
        let removed = this._entries.splice(startIndex, howMany);
        this._rows -= howMany;
        return removed;
    }
    removeColumns(startIndex: number, howMany = 1) {
        let removed = this._entries.map(row => row.splice(startIndex, howMany));
        this._cols -= howMany;
        return removed;
    }
    shrink(rows: number = 0, cols: number = 0): this {
        this._rows = Math.max(this._rows - rows, 0);
        this._cols = Math.max(this._cols - cols, 0);
        this._entries = this._entries.slice(0, this.rows);
        this._entries = this._entries.map(row => row.slice(0, this.cols));
        return this;
    }
    pretty(formatter?: (v: T) => string): string {
        let f = formatter ? formatter : this.formatter;
        let val = "";
        for(let row of this._entries) {
            val += "[ " + row.map(e => e != null ? f(e) : "").join(" ") + " ]\n";
        }
        return val;
    }
}

export class NumericMatrix extends Matrix<number> {}


// WEBPACK FOOTER //
// ./src/code/Math/Matrix.ts